import React, { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function ImagePreviewEditor({
  dropImg,
  imageUrl,
  onDrop,
  imgProps, 
  setImgProps, 
  tracks,
  imageIndex,
  project,
  imageProcess,
  removeImage
}) {

  const PreviewText = ({style}) => {
    let text = '';
    if (project.print_title) text = tracks[imageIndex].name;

    if (text.match(/^\d@/)) {
      text = text.substr(2, 48);
      const pp = text.indexOf('+');
      if (pp>-1) {
        text = text.substr(0, pp);
      }
      else {
        if (text.length > 25) {
          text = text.substr(0, 25);
          const ps = text.lastIndexOf(' ');
          if (ps>-1) {  
            text = text.substr(0, ps);
          }
        }
      }
    }
    else {
      if (text.length > 30) {
        text = text.substr(0, 30);
        const ps = text.lastIndexOf(' ');
        if (ps>-1) {  
          text = text.substr(0, ps);
        }
      }
    }

    if (project.print_track && tracks[imageIndex].pos > 1) text = (tracks[imageIndex].pos-1)+(text===''?'':'. ')+ text;
    return (
      <p style={{...style, textAlign:'center', fontSize:'1.5em', margin:'0', overflow:'hidden', whiteSpace:'nowrap', width:'100%'}}>{text?text:<span>&nbsp;</span>}</p>
    );
  }

  const frameRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [didDrag, setDidDrag] = useState(false);
  const [startMouse, setStartMouse] = useState({ x: 0, y: 0 });
  const [startOffset, setStartOffset] = useState({ x: 0, y: 0 });

  const [frameSize, setFrameSize] = useState({ w: 1, h: 1 });
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });

  const hasText = project.print_title || project.print_track;

  const setOffset = (offset) => {
    setImgProps({
      ...imgProps,
      offset,
    });
  }

  const setScale = (scale) => {
    setImgProps({scale, offset: {x:0, y:0}});
  }

  useEffect(() => {
    if (!imageUrl) {
      setImgSize({ w: 1, h: 1 });
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    const updateSize = () => {
      if (!frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect();
      setFrameSize({
        w: rect.width || 1,
        h: rect.height || 1,
      });
    };

    updateSize();

    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [hasText]);

  const frameRatio = frameSize.w / frameSize.h;
  const imgRatio = imgSize.w / imgSize.h;

  let baseW = frameSize.w;
  let baseH = frameSize.h;

  if (imgRatio > frameRatio) {
    baseH = frameSize.h;
    baseW = frameSize.h * imgRatio;
  } else {
    baseW = frameSize.w;
    baseH = frameSize.w / imgRatio;
  }

  const renderedW = baseW * imgProps.scale;
  const renderedH = baseH * imgProps.scale;

  // aktueller Abstand zwischen Bildgröße und Rahmen
  const maxOffsetX = Math.abs(renderedW - frameSize.w);
  const maxOffsetY = Math.abs(renderedH - frameSize.h);

  // gewünschte Regel: maximale Verschiebung = +/- maxOffset * 2
  const panLimitX = frameSize.w * imgProps.scale / 2;
  const panLimitY = frameSize.h * imgProps.scale / 2;

console.log('frameSize', frameSize, 'imgSize', imgSize, 'base', baseW, baseH, 'rendered', renderedW, renderedH, 'maxOffset', maxOffsetX, maxOffsetY, 'panLimit', panLimitX, panLimitY);  

  const canPan = true; //panLimitX > 0.5 || panLimitY > 0.5;
  useEffect(() => {
    if (frameSize.w<2) return;
    setImgProps((prev) => ({
      ...prev, offset: {
      x: clamp(prev.offset.x, -panLimitX, panLimitX),
      y: clamp(prev.offset.y, -panLimitY, panLimitY),
    }}));
  }, [panLimitX, panLimitY]);

  const zoomIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgProps((prev) => ({...prev, scale: Math.min(prev.scale + 0.2, 4)}));
  };

  const zoomOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImgProps((prev) => ({...prev, scale: Math.max(prev.scale - 0.2, 0.2)}));
  };

  const onMouseDown = (e) => {
    if (!canPan) return;

    e.preventDefault();
    e.stopPropagation();

    setDragging(true);
    setDidDrag(false);
    setStartMouse({ x: e.clientX, y: e.clientY });
    setStartOffset({ x: imgProps.offset.x, y: imgProps.offset.y });
  };

  const onMouseMove = (e) => {
    if (!dragging) return;

    e.preventDefault();

    const dx = e.clientX - startMouse.x;
    const dy = e.clientY - startMouse.y;

    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      setDidDrag(true);
    }

    setOffset({
      x: clamp(startOffset.x + dx, -panLimitX, panLimitX),
      y: clamp(startOffset.y + dy, -panLimitY, panLimitY),
    });
  };

  const endDrag = (e) => {
    if (dragging && e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDragging(false);
  };

  const onClickCapture = (e) => {
    if (didDrag) {
      e.preventDefault();
      e.stopPropagation();
      setDidDrag(false);
    }
  };

  return (
    <td colSpan={2}>
      <div
        style={{
          backgroundColor: tracks[imageIndex].background,
          width: "192px",
          height: "192px",
          borderRadius: "0.5em",
          border: "4px solid " + tracks[imageIndex].frame,
          marginLeft: "2em",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent:
            project.print_title || project.print_track
              ? (project.has_image === 1 ? "flex-end" : "flex-start")
              : "center",
        }}
      >
        {project.has_image === 1 && hasText && (
          <PreviewText style={{ position: "absolute", top: "-0.2em" }} />
        )}

        {imageProcess ? (
          <div className="text-center" style={{ fontSize: "2em", marginBottom: "0.75em" }}>
            <svg className="circlespinner" viewBox="0 0 50 50">
              <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
            </svg>
          </div>
        ) : (
          <div
            {...dropImg.getRootProps({
              className: "dropzone",
              onClickCapture,
            })}
          >
            <input {...dropImg.getInputProps()} />

            <div
              style={{
                border: "2px dashed #CCC",
                padding: "0.2em",
                position: "relative",
              }}
            >
              <div
                ref={frameRef}
                style={{
                  width: "100%",
                  aspectRatio: hasText ? "6 / 5" : "1 / 1",
                  overflow: "hidden",
                  position: "relative",
                  cursor: canPan ? (dragging ? "grabbing" : "grab") : "default",
                }}
                onMouseMove={onMouseMove}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    draggable={false}
                    onMouseDown={onMouseDown}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                      width: `${baseW}px`,
                      height: `${baseH}px`,
                      userSelect: "none",
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: `translate(calc(-50% + ${imgProps.offset.x}px), calc(-50% + ${imgProps.offset.y}px)) scale(${imgProps.scale})`,
                      transformOrigin: "center center",
                    }}
                  />
                ) : (
                  <p
                    style={{
                      display: "block",
                      padding: "1em",
                      textAlign: "center",
                      margin: "0.5em",
                    }}
                  >
                    Bild hier ablegen, oder hier klicken
                    <br />
                    PNG oder JPG
                    <br />
                    Querformat 6 : 5
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {project.has_image === 2 && hasText && (
          <PreviewText style={{ position: "absolute", bottom: "-0.05em" }} />
        )}

        <div
          style={{
            marginBottom: "0.5em",
            display: "flex",
            flexDirection: "column",
            gap: "1em",
            position: "absolute",
            bottom: "0.0",
            right: "-40px",
          }}
        >
          <button type="button" className="zoom-button" onClick={()=>setScale(1)}>
            <svg class="bi bi-search" width="30" height="30" fill="currentColor" viewBox="0 0 16 16"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/><path fill="#000" d="M2.8891 8.9042V8.3067H4.2904V4.0717L3.0491 4.9592V4.2942L4.3491 3.4004H4.9979V8.3067H6.3379V8.9042Z"/><path fill="#000" d="M8.9406 8.9042L7.9938 7.4583 7.0396 8.9042H6.4083L7.6615 7.0937 6.4667 5.3823H7.1146L7.9938 6.7521 8.8656 5.3823H9.5198L8.3261 7.0875 9.5948 8.9042Z"/></svg>
          </button>
          <button type="button" className="bi-zoom-out zoom-button" onClick={zoomOut} />
          <button type="button" className="bi-zoom-in zoom-button" onClick={zoomIn} />
        </div>
        <button type="button" className="btn-close" onClick={() => removeImage(tracks[imageIndex].id)} style={{ position: 'absolute', left: '-33px', bottom: 0}}/>
      </div>
    </td>
  );
}